import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { X, MapPin, Navigation, Loader2, Clock } from 'lucide-react';
import { useLocation } from '@context/LocationContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import loadGoogleMaps from '@utils/loadGoogleMaps';
import {
  addLocationSearchHistory,
  matchLocationSearchHistory,
  mergeHistoryWithApiPredictions,
} from '@utils/locationSearchHistory';

const GooglePlacesSearch = ({
  onPlaceSelect,
  onClear,
  placeholder = 'Search location...',
  initialValue = '',
}) => {
  const { googleMapsApiKey } = usePublicConfig();
  const {
    location: userLoc,
    locationName,
    locationLoading,
    resetToDeviceLocation,
    forwardGeocode,
  } = useLocation();

  const [query, setQuery] = useState(initialValue || '');
  const [apiResults, setApiResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [searching, setSearching] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const autoRef = useRef(null);
  const placesRef = useRef(null);
  const tokenRef = useRef(null);
  const debounceRef = useRef(null);
  const pendingGpsHistoryRef = useRef(false);
  const lastInitialValueRef = useRef(initialValue);

  useEffect(() => {
    if (typeof initialValue !== 'string') return;
    if (initialValue === lastInitialValueRef.current) return;
    lastInitialValueRef.current = initialValue;
    setQuery(initialValue);
  }, [initialValue]);

  const historyMatches = useMemo(
    () => (query.trim().length >= 2 ? matchLocationSearchHistory(query) : []),
    [query],
  );

  const mergedResults = useMemo(
    () => mergeHistoryWithApiPredictions(historyMatches, apiResults, 5),
    [historyMatches, apiResults],
  );

  // ── Load Google Maps JS & init Places services ──────────────────────
  useEffect(() => {
    if (!googleMapsApiKey) return;
    loadGoogleMaps(googleMapsApiKey)
      .then(() => {
        setGoogleReady(true);
        autoRef.current = new window.google.maps.places.AutocompleteService();
        const div = document.createElement('div');
        placesRef.current = new window.google.maps.places.PlacesService(div);
        tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      })
      .catch(() => {});
  }, [googleMapsApiKey]);

  const resetSessionToken = useCallback(() => {
    if (window.google?.maps?.places) {
      tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  // ── Close on click outside ──────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── GPS detect complete: history + parent callback ──────────────────
  useEffect(() => {
    if (!locationLoading && detecting) {
      const fromGpsButton = pendingGpsHistoryRef.current;
      pendingGpsHistoryRef.current = false;
      setDetecting(false);
      if (fromGpsButton) {
        if (
          userLoc?.lat != null &&
          userLoc?.lng != null &&
          locationName &&
          !['Detecting...', 'Location unavailable'].includes(locationName)
        ) {
          const parts = locationName.split(',').map((s) => s.trim());
          addLocationSearchHistory({
            mainText: parts[0] || locationName,
            secondaryText: parts.slice(1).join(', '),
            lat: userLoc.lat,
            lng: userLoc.lng,
          });
        }
        onPlaceSelect({
          lat: userLoc.lat,
          lng: userLoc.lng,
          name: locationName || 'Current location',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationLoading, detecting, userLoc, locationName]);

  // ── Autocomplete search (biased to current city) ────────────────────
  const searchGoogle = useCallback(
    (value) => {
      if (!autoRef.current) return;
      setSearching(true);

      const request = {
        input: value,
        sessionToken: tokenRef.current,
        componentRestrictions: { country: 'in' },
      };

      if (userLoc?.lat && userLoc?.lng) {
        request.location = new window.google.maps.LatLng(userLoc.lat, userLoc.lng);
        request.radius = 50000;
      }

      autoRef.current.getPlacePredictions(request, (predictions, status) => {
        setSearching(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setApiResults(
            predictions.map((p) => ({
              id: p.place_id,
              name: p.structured_formatting?.main_text || p.description,
              description: p.structured_formatting?.secondary_text || '',
              placeId: p.place_id,
            })),
          );
        } else {
          setApiResults([]);
        }
      });
    },
    [userLoc],
  );

  const searchFallback = useCallback(
    async (value) => {
      setSearching(true);
      const items = await forwardGeocode(value);
      setSearching(false);
      setApiResults(
        items.map((r) => ({
          id: `${r.lat}-${r.lng}`,
          name: r.name.split(',')[0],
          description: r.name.split(',').slice(1, 3).join(',').trim(),
          lat: r.lat,
          lng: r.lng,
        })),
      );
    },
    [forwardGeocode],
  );

  // ── Debounced input handler ─────────────────────────────────────────
  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setQuery(val);
      if (!val.trim()) {
        setApiResults([]);
        return;
      }
      setOpen(true);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (googleReady) searchGoogle(val);
        else searchFallback(val);
      }, 300);
    },
    [googleReady, searchGoogle, searchFallback],
  );

  // ── Clear stale API when query too short ───────────────────────────
  useEffect(() => {
    if (query.trim().length < 2) setApiResults([]);
  }, [query]);

  // ── Select a result ─────────────────────────────────────────────────
  const handleSelect = useCallback(
    (result) => {
      const label = [result.mainText, result.secondaryText].filter(Boolean).join(', ');
      setQuery(result.mainText || result.name || '');
      setApiResults([]);
      setOpen(false);
      inputRef.current?.blur();

      if (result.isHistory && result.lat != null && result.lng != null) {
        onPlaceSelect({
          lat: result.lat,
          lng: result.lng,
          name: label || result.mainText,
        });
        addLocationSearchHistory({
          mainText: result.mainText,
          secondaryText: result.secondaryText,
          lat: result.lat,
          lng: result.lng,
          placeId: result.placeId,
        });
        return;
      }

      if (result.placeId && placesRef.current) {
        placesRef.current.getDetails(
          { placeId: result.placeId, fields: ['geometry'] },
          (place, status) => {
            if (status === 'OK' && place?.geometry?.location) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              onPlaceSelect({
                lat,
                lng,
                name: result.mainText || result.name,
              });
              addLocationSearchHistory({
                mainText: result.mainText || result.name,
                secondaryText: result.secondaryText || result.description,
                lat,
                lng,
                placeId: result.placeId,
              });
              resetSessionToken();
            }
          },
        );
      } else if (result.lat != null && result.lng != null) {
        onPlaceSelect({
          lat: result.lat,
          lng: result.lng,
          name: result.mainText || result.name,
        });
        addLocationSearchHistory({
          mainText: result.mainText || result.name,
          secondaryText: result.secondaryText || result.description,
          lat: result.lat,
          lng: result.lng,
        });
      }
    },
    [onPlaceSelect, resetSessionToken],
  );

  // ── Detect current location ─────────────────────────────────────────
  const handleDetectLocation = useCallback(() => {
    pendingGpsHistoryRef.current = true;
    setDetecting(true);
    resetToDeviceLocation();
    setQuery('');
    setApiResults([]);
    setOpen(false);
    resetSessionToken();
  }, [resetToDeviceLocation, resetSessionToken]);

  const clear = () => {
    setQuery('');
    setApiResults([]);
    setOpen(false);
    onClear?.();
  };

  const showSuggestions = open && query.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative">
      {/* ── Input bar ──────────────────────────────────────────────── */}
      <div className="relative">
        <MapPin
          size={15}
          strokeWidth={2}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 dark:text-surface-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={() => {
            if (query.trim().length >= 2) setOpen(true);
          }}
          className="w-full rounded-xl border border-surface-200/70 bg-white/95 py-2.5 pl-9 pr-16 text-sm text-surface-800 placeholder:text-surface-400 transition-all focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 backdrop-blur-xl shadow-sm dark:bg-surface-800/90 dark:border-surface-700/50 dark:text-white dark:placeholder:text-surface-500"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {query && (
            <button
              type="button"
              onClick={clear}
              className="flex items-center justify-center w-7 h-7 rounded-lg text-surface-400 hover:bg-surface-100 hover:text-surface-600 dark:hover:bg-surface-700 dark:hover:text-surface-300 transition-colors"
            >
              <X size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={handleDetectLocation}
            disabled={detecting || locationLoading}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-surface-400 hover:bg-surface-100 hover:text-brand-500 dark:hover:bg-surface-700 dark:hover:text-brand-400 transition-colors disabled:opacity-50"
            title="Use my current location"
          >
            {detecting || locationLoading ? (
              <Loader2 size={14} className="animate-spin text-brand-500" />
            ) : (
              <Navigation size={14} />
            )}
          </button>
        </div>
      </div>

      {/* ── Dropdown ───────────────────────────────────────────────── */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border border-surface-200/80 bg-white shadow-xl dark:border-surface-700 dark:bg-surface-900 overflow-hidden">
          {mergedResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800">
              {mergedResults.map((r) => (
                <button
                  key={r.listKey}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(r)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800/80 transition-colors"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 dark:bg-surface-800">
                    {r.isHistory ? (
                      <Clock size={15} strokeWidth={2} className="text-surface-500 dark:text-surface-400" />
                    ) : (
                      <MapPin size={15} strokeWidth={2} className="text-surface-500 dark:text-surface-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-surface-800 dark:text-white truncate">
                      {r.mainText}
                    </div>
                    {r.secondaryText ? (
                      <div className="text-[11px] text-surface-400 truncate">
                        {r.secondaryText}
                      </div>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searching && (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-surface-400 border-t border-surface-100 dark:border-surface-800">
              <Loader2 size={14} className="animate-spin" />
              Finding places...
            </div>
          )}
          {!searching && mergedResults.length === 0 && (
            <div className="py-4 text-center text-xs text-surface-400">
              No places found for &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GooglePlacesSearch;
